import React, { useMemo } from "react";
import PropTypes from "prop-types";
import uniqid from "uniqid";

const AiGlossyOrb = ({ size = 220, className, style }) => {
    // 같은 페이지에 2개 이상 렌더돼도 SVG id가 충돌하지 않도록 인스턴스별 고유 접두사 사용
    const uid = useMemo(() => uniqid("orb-"), []);

    return (
        <div
            className={className}
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                ...style,
            }}
        >
            <style>{`
                @keyframes orbBreath {
                    0%, 100% { transform: scale(0.95) rotate(-1.5deg); }
                    50%      { transform: scale(1.05) rotate(1.5deg); }
                }
                @keyframes orbDriftFast { to { transform: rotate(360deg); } }
                @keyframes orbDriftRev  { to { transform: rotate(-360deg); } }
            `}</style>

            <div
                style={{
                    position: "relative",
                    width: size,
                    height: size,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <div style={{ animation: "orbBreath 2.6s ease-in-out infinite" }}>
                    <svg viewBox="0 0 240 240" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <radialGradient id={`${uid}sphereBase`} cx="38%" cy="32%" r="78%">
                                <stop offset="0%" stopColor="#bfe6ff" />
                                <stop offset="38%" stopColor="#4f8df0" />
                                <stop offset="70%" stopColor="#6a4bd6" />
                                <stop offset="100%" stopColor="#3a2a8c" />
                            </radialGradient>
                            <linearGradient id={`${uid}swirlGrad`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#d9f6ff" />
                                <stop offset="55%" stopColor="#3fd0ff" />
                                <stop offset="100%" stopColor="#1aa6f0" />
                            </linearGradient>
                            <clipPath id={`${uid}orbClip`}>
                                <circle cx="120" cy="120" r="92" />
                            </clipPath>
                            <filter id={`${uid}soft`} x="-120%" y="-120%" width="340%" height="340%">
                                <feGaussianBlur stdDeviation="14" />
                            </filter>
                            <filter id={`${uid}softer`} x="-150%" y="-150%" width="400%" height="400%">
                                <feGaussianBlur stdDeviation="20" />
                            </filter>
                        </defs>

                        <circle cx="120" cy="120" r="92" fill={`url(#${uid}sphereBase)`} />

                        <g clipPath={`url(#${uid}orbClip)`}>
                            <g
                                style={{
                                    transformOrigin: "120px 120px",
                                    animation: "orbDriftFast 6s linear infinite",
                                }}
                            >
                                <ellipse
                                    cx="72"
                                    cy="92"
                                    rx="74"
                                    ry="86"
                                    fill="#8a3fe0"
                                    opacity="0.85"
                                    filter={`url(#${uid}soft)`}
                                />
                                <ellipse
                                    cx="96"
                                    cy="186"
                                    rx="78"
                                    ry="60"
                                    fill="#cf4fd6"
                                    opacity="0.7"
                                    filter={`url(#${uid}soft)`}
                                />
                                <ellipse
                                    cx="186"
                                    cy="128"
                                    rx="80"
                                    ry="92"
                                    fill="#1ec8ff"
                                    opacity="0.9"
                                    filter={`url(#${uid}soft)`}
                                />
                            </g>
                            <g
                                style={{
                                    transformOrigin: "120px 120px",
                                    animation: "orbDriftRev 9s linear infinite",
                                }}
                            >
                                <ellipse
                                    cx="128"
                                    cy="66"
                                    rx="56"
                                    ry="44"
                                    fill="#c46be8"
                                    opacity="0.55"
                                    filter={`url(#${uid}softer)`}
                                />
                                <ellipse
                                    cx="150"
                                    cy="205"
                                    rx="70"
                                    ry="55"
                                    fill="#2f7bf0"
                                    opacity="0.6"
                                    filter={`url(#${uid}soft)`}
                                />
                                <ellipse
                                    cx="80"
                                    cy="150"
                                    rx="46"
                                    ry="60"
                                    fill="#16d6e8"
                                    opacity="0.55"
                                    filter={`url(#${uid}softer)`}
                                />
                            </g>

                            <path
                                fill="none"
                                stroke={`url(#${uid}swirlGrad)`}
                                strokeWidth="30"
                                strokeLinecap="round"
                                opacity="0.9"
                                filter={`url(#${uid}soft)`}
                            >
                                <animate
                                    attributeName="d"
                                    dur="3.2s"
                                    repeatCount="indefinite"
                                    calcMode="spline"
                                    keyTimes="0;0.5;1"
                                    keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
                                    values="M158 52 C 112 80 96 112 122 132 C 150 154 142 190 126 214;
                                            M150 50 C 132 86 80 116 116 138 C 158 162 138 188 138 216;
                                            M158 52 C 112 80 96 112 122 132 C 150 154 142 190 126 214"
                                />
                            </path>
                            <path
                                fill="none"
                                stroke="#eafaff"
                                strokeWidth="9"
                                strokeLinecap="round"
                                opacity="0.8"
                                filter={`url(#${uid}softer)`}
                            >
                                <animate
                                    attributeName="d"
                                    dur="3.2s"
                                    repeatCount="indefinite"
                                    calcMode="spline"
                                    keyTimes="0;0.5;1"
                                    keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
                                    values="M162 60 C 122 84 108 112 128 130 C 150 150 146 182 134 204;
                                            M156 58 C 138 90 96 116 124 134 C 156 156 142 182 144 206;
                                            M162 60 C 122 84 108 112 128 130 C 150 150 146 182 134 204"
                                />
                            </path>

                            <ellipse
                                cx="98"
                                cy="74"
                                rx="58"
                                ry="38"
                                fill="#ffffff"
                                opacity="0.4"
                                filter={`url(#${uid}soft)`}
                            >
                                <animate
                                    attributeName="opacity"
                                    values="0.25;0.55;0.25"
                                    dur="2.2s"
                                    repeatCount="indefinite"
                                />
                            </ellipse>
                            <ellipse
                                cx="86"
                                cy="58"
                                rx="26"
                                ry="15"
                                fill="#ffffff"
                                opacity="0.55"
                                filter={`url(#${uid}softer)`}
                            />
                        </g>

                        <circle cx="120" cy="120" r="92" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.25" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

AiGlossyOrb.propTypes = {
    size: PropTypes.number,
    className: PropTypes.string,
    style: PropTypes.object,
};

export default AiGlossyOrb;
